import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  AnnotationOverlay,
  type Annotation,
} from "../components/AnnotationOverlay";
import { useDebouncedValue } from "../lib/hooks";
import { trpc } from "../trpc";

type Point = { x: number; y: number };
type Box = { x: number; y: number; w: number; h: number };

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

/** A normalised {x,y,w,h} box (fractions 0..1) from two corner points. */
function boxFrom(a: Point, b: Point): Box {
  const x = clamp01(Math.min(a.x, b.x));
  const y = clamp01(Math.min(a.y, b.y));
  const x2 = clamp01(Math.max(a.x, b.x));
  const y2 = clamp01(Math.max(a.y, b.y));
  return { x, y, w: x2 - x, h: y2 - y };
}

function rectStyle(box: Box): CSSProperties {
  return {
    left: `${box.x * 100}%`,
    top: `${box.y * 100}%`,
    width: `${box.w * 100}%`,
    height: `${box.h * 100}%`,
  };
}

/**
 * The heart of the capture flow: shows the photographed doha and lets
 * the reader mark words on it — either by tapping a word the doha is
 * already known to contain, or by dragging a box on the image and then
 * linking it to a glossary word or flagging it as needing a definition.
 */
export function AnnotationEditor({
  captureId,
  mediaPath,
  dohaSlug,
  onDone,
}: {
  captureId: number;
  mediaPath: string;
  dohaSlug: string | null;
  /** Called by the Done button. Defaults to navigating to the capture's
   *  page — pass a handler to reuse the editor in place (e.g. exit an
   *  edit mode on the capture view). */
  onDone?: () => void;
}) {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const handleDone = onDone ?? (() => navigate(`/capture/${captureId}`));

  const dohaQuery = trpc.dohas.get.useQuery(
    { slug: dohaSlug ?? "" },
    { enabled: dohaSlug !== null },
  );
  const terms = dohaQuery.data?.terms ?? [];

  const listQuery = trpc.annotations.listForCapture.useQuery({ id: captureId });
  const annotations: Annotation[] = (listQuery.data ?? []).map((a) => ({
    id: a.id,
    status: a.status === "linked" ? "linked" : "undefined",
    label: a.label,
    termId: a.termId,
    box: a.box,
  }));
  const linkedLabels = new Set(
    annotations.filter((a) => a.status === "linked").map((a) => a.label),
  );

  const [confirmed, setConfirmed] = useState<Set<string>>(new Set());
  const [drawStart, setDrawStart] = useState<Point | null>(null);
  const [drawNow, setDrawNow] = useState<Point | null>(null);
  const [pendingBox, setPendingBox] = useState<Box | null>(null);
  const [mode, setMode] = useState<"link" | "undefined">("link");
  const [linkQuery, setLinkQuery] = useState("");
  const [undefLabel, setUndefLabel] = useState("");

  const debLink = useDebouncedValue(linkQuery.trim());
  const glossaryQuery = trpc.glossary.search.useQuery(
    { q: debLink, limit: 8 },
    { enabled: mode === "link" && pendingBox !== null && debLink.length > 0 },
  );

  const createAnn = trpc.annotations.create.useMutation({
    onSuccess: (_data, variables) => {
      void utils.annotations.listForCapture.invalidate({ id: captureId });
      if (variables.status === "linked" && variables.termSlug) {
        const slug = variables.termSlug;
        setConfirmed((prev) => {
          const next = new Set(prev);
          next.add(slug);
          return next;
        });
      }
      setPendingBox(null);
      setLinkQuery("");
      setUndefLabel("");
    },
  });

  function fracFromEvent(e: ReactPointerEvent<HTMLDivElement>): Point {
    const rect = e.currentTarget.getBoundingClientRect();
    return {
      x: clamp01((e.clientX - rect.left) / rect.width),
      y: clamp01((e.clientY - rect.top) / rect.height),
    };
  }

  function onPointerDown(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = fracFromEvent(e);
    setDrawStart(p);
    setDrawNow(p);
  }

  function onPointerMove(e: ReactPointerEvent<HTMLDivElement>) {
    if (!drawStart) return;
    setDrawNow(fracFromEvent(e));
  }

  function onPointerUp(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (drawStart && drawNow) {
      const box = boxFrom(drawStart, drawNow);
      if (box.w >= 0.02 && box.h >= 0.02) {
        setPendingBox(box);
        setMode("link");
        setLinkQuery("");
        setUndefLabel("");
      }
    }
    setDrawStart(null);
    setDrawNow(null);
  }

  function tapChip(slug: string, displayName: string) {
    createAnn.mutate({
      captureId,
      status: "linked",
      label: displayName,
      termSlug: slug,
    });
  }

  function linkPendingBox(slug: string, displayName: string) {
    if (!pendingBox) return;
    createAnn.mutate({
      captureId,
      status: "linked",
      label: displayName,
      termSlug: slug,
      box: pendingBox,
    });
  }

  function flagPendingBox() {
    const label = undefLabel.trim();
    if (!pendingBox || label.length === 0) return;
    createAnn.mutate({
      captureId,
      status: "undefined",
      label,
      box: pendingBox,
    });
  }

  const liveBox = drawStart && drawNow ? boxFrom(drawStart, drawNow) : null;

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-line pb-6">
        <div>
          <p className="eyebrow text-ink-faint">Mark the words</p>
          <h1 className="mt-1 font-serif text-3xl text-ink">
            {dohaSlug ? (
              <>
                Reading <span className="font-mono text-2xl">{dohaSlug}</span>
              </>
            ) : (
              "This capture"
            )}
          </h1>
        </div>
        <button
          type="button"
          onClick={handleDone}
          className="rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90"
        >
          Done
        </button>
      </div>

      <p className="mt-6 text-sm text-ink-faint">
        Drag across a word on the photo to mark it — link it to a known word, or
        flag one the glossary is missing.
      </p>

      <div className="mt-4 flex justify-center rounded-2xl border border-line bg-paper-sunk p-4">
        <div className="relative inline-block select-none">
          <AnnotationOverlay mediaPath={mediaPath} annotations={annotations} />
          <div
            className="absolute inset-0 cursor-crosshair touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {liveBox ? (
              <div
                className="pointer-events-none absolute rounded-sm border-2 border-dashed border-terracotta"
                style={{
                  ...rectStyle(liveBox),
                  background: "rgba(193, 95, 60, 0.12)",
                }}
              />
            ) : pendingBox ? (
              <div
                className="pointer-events-none absolute rounded-sm border-2 border-terracotta"
                style={{
                  ...rectStyle(pendingBox),
                  background: "rgba(193, 95, 60, 0.16)",
                }}
              />
            ) : null}
          </div>
        </div>
      </div>

      {pendingBox ? (
        <div className="mt-5 rounded-2xl border border-terracotta bg-paper-raised p-5">
          <div className="flex items-center justify-between gap-4">
            <p className="eyebrow text-ink-faint">Mark this word</p>
            <button
              type="button"
              onClick={() => setPendingBox(null)}
              className="text-sm text-ink-faint transition-colors hover:text-ink"
            >
              Discard box
            </button>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              type="button"
              onClick={() => setMode("link")}
              className={
                mode === "link"
                  ? "rounded-full bg-ink px-4 py-1.5 text-sm font-medium text-paper"
                  : "rounded-full border border-line-strong px-4 py-1.5 text-sm text-ink transition-colors hover:bg-paper-sunk"
              }
            >
              Link a word
            </button>
            <button
              type="button"
              onClick={() => setMode("undefined")}
              className={
                mode === "undefined"
                  ? "rounded-full bg-ink px-4 py-1.5 text-sm font-medium text-paper"
                  : "rounded-full border border-line-strong px-4 py-1.5 text-sm text-ink transition-colors hover:bg-paper-sunk"
              }
            >
              Needs a definition
            </button>
          </div>

          {mode === "link" ? (
            <div className="mt-4">
              <input
                type="search"
                value={linkQuery}
                onChange={(e) => setLinkQuery(e.target.value)}
                placeholder="Search the glossary…"
                aria-label="Search the glossary"
                className="w-full rounded-xl border border-line-strong bg-paper px-4 py-2.5 font-serif text-lg text-ink outline-none transition focus:border-terracotta/60 placeholder:text-ink-faint/70"
              />
              {debLink.length > 0 ? (
                <div className="mt-3 overflow-hidden rounded-xl border border-line">
                  {glossaryQuery.isLoading ? (
                    <p className="px-4 py-3 text-sm text-ink-faint">
                      Searching…
                    </p>
                  ) : glossaryQuery.data && glossaryQuery.data.length > 0 ? (
                    <ul className="divide-y divide-line">
                      {glossaryQuery.data.map((term) => (
                        <li key={term.slug}>
                          <button
                            type="button"
                            disabled={createAnn.isPending}
                            onClick={() =>
                              linkPendingBox(term.slug, term.displayName)
                            }
                            className="flex w-full items-center justify-between px-4 py-2.5 text-left transition-colors hover:bg-paper-sunk disabled:opacity-50"
                          >
                            <span className="font-serif text-lg text-ink">
                              {term.displayName}
                            </span>
                            {term.isStub ? (
                              <span className="eyebrow text-ink-faint/70">
                                unwritten
                              </span>
                            ) : null}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="px-4 py-3 text-sm text-ink-faint">
                      No word matches “{debLink}”.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          ) : (
            <div className="mt-4">
              <label className="eyebrow text-ink-faint" htmlFor="undef-label">
                The word as written
              </label>
              <div className="mt-2 flex gap-2">
                <input
                  id="undef-label"
                  type="text"
                  value={undefLabel}
                  onChange={(e) => setUndefLabel(e.target.value)}
                  placeholder="e.g. Divyadrishti"
                  className="w-full rounded-xl border border-line-strong bg-paper px-4 py-2.5 font-serif text-lg text-ink outline-none transition focus:border-terracotta/60 placeholder:text-ink-faint/70"
                />
                <button
                  type="button"
                  disabled={
                    undefLabel.trim().length === 0 || createAnn.isPending
                  }
                  onClick={flagPendingBox}
                  className="shrink-0 rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-50"
                >
                  Flag it
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {createAnn.error ? (
        <p className="mt-3 text-sm text-terracotta">
          {createAnn.error.message}
        </p>
      ) : null}

      {dohaSlug ? (
        <section className="mt-8">
          <h2 className="eyebrow text-ink-faint">Words known in this doha</h2>
          {dohaQuery.isLoading ? (
            <p className="mt-3 text-sm text-ink-faint">
              Loading the doha’s words…
            </p>
          ) : terms.length > 0 ? (
            <>
              <p className="mt-1 text-sm text-ink-faint">
                Tap a word to confirm it appears on the page.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {terms.map((term) => {
                  const isConfirmed =
                    confirmed.has(term.slug) ||
                    linkedLabels.has(term.displayName);
                  return (
                    <button
                      key={term.slug}
                      type="button"
                      disabled={isConfirmed || createAnn.isPending}
                      onClick={() => tapChip(term.slug, term.displayName)}
                      className={
                        isConfirmed
                          ? "rounded-full border border-terracotta bg-terracotta-wash px-3.5 py-1.5 font-serif text-terracotta"
                          : "rounded-full border border-line-strong px-3.5 py-1.5 font-serif text-ink transition-colors hover:bg-paper-sunk disabled:opacity-50"
                      }
                    >
                      {isConfirmed ? "✓ " : ""}
                      {term.displayName}
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="mt-3 text-sm text-ink-faint">
              No glossary words are linked to this doha yet — draw boxes to mark
              the words you find.
            </p>
          )}
        </section>
      ) : null}

      <div className="mt-10 flex items-center justify-between border-t border-line pt-6">
        <p className="text-sm text-ink-faint">
          {annotations.length} {annotations.length === 1 ? "word" : "words"}{" "}
          marked
        </p>
        <button
          type="button"
          onClick={handleDone}
          className="rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90"
        >
          Done
        </button>
      </div>
    </div>
  );
}
